import { Connection } from 'mysql2/promise';
import { BusinessRuleItemType } from '../constants/globals';
import { KnownClearingHouses } from './BillingCode.type';
import { RecordAnalyticsParams } from './ClinicActivity.type';
import { ConnectorTraceCollector } from './ConnectorTrace.type';
import {
  EligibilityBenefitResponse,
  EligibilityRequestContext,
  InsuranceEligibilityRequestStatus,
} from './InsuranceEligibilityRequest.type';
import {
  IntegrationExchangeRow,
  IntegrationExchangeRowPartial,
  IntegrationExchangeTypeEnum,
} from './Integration.type';
import { Pseudo835Scenario, Pseudo835ScenarioOption } from './Pseudo835.type';

/**
 * Integration connector contract — the vendor-agnostic seam for API-first
 * clearinghouses (docs/connection-api/INFRASTRUCTURE_PLAN.md §3a).
 *
 * Vendor implementations live ONLY under
 * `libs/server-core/.../integration.helpers/connector/vendors/<vendor>/` and
 * are registered as ONE public factory per vendor. Generic code never
 * references a vendor by name (pinning test enforces this).
 */

export enum ConnectorScope {
  /** One ChiroUp account at the vendor; credentials/polling are platform-wide. */
  Platform = 'platform',
  /** Credentials live on each clinic's Integration row (no current vendor). */
  Clinic = 'clinic',
}

export enum IntegrationDocumentType {
  Claim837P = '837P',
  Remit835 = '835',
  Ack277CA = '277CA',
  Ack999 = '999',
  Req270 = '270',
  Res271 = '271',
  /**
   * A document the vendor sent whose transaction set we do not recognize. NOT a
   * placeholder for "unsupported" — a named type with no handler stays named. This
   * exists so an unrecognized document is PERSISTED and visible rather than dropped
   * at the poll: nothing a vendor sends is thrown away (plan §4.3). Fails every
   * support gate by construction, so it can never offer a reprocess.
   */
  Unknown = 'UNK',
}

/**
 * How big one submission to the vendor may be, per document type. `max`
 * is a vendor fact declared on the factory — the batch queue pays a user's
 * selection (any size) out in chunks of this many, and `createBatchSet`
 * chokes if handed more. There is no `min` (one is the floor everywhere) and
 * no per-clinic knob.
 */
export type ConnectorSubmitLimit = { max: number };

export type ConnectorCapabilities = {
  /** Doc types this vendor can submit, each with its per-submission limit. Undeclared = cannot submit. */
  submit: Partial<Record<IntegrationDocumentType, ConnectorSubmitLimit>>;
  /**
   * Doc types this vendor's `parseDocument` can map to a canonical payload. HALF of
   * the support question — the other half is whether a dispatcher exists to APPLY
   * that payload, which is global, not per-vendor. `isInboundDocTypeSupported` ANDs
   * the two; consult that, never this alone.
   *
   * Declared rather than inferred: `parseDocument` throwing on an unsupported type is
   * the runtime enforcement, but a queue read needs the answer WITHOUT parsing.
   */
  ingest: IntegrationDocumentType[];
  poll: boolean;
  webhook: boolean;
};

/**
 * Pointer to one inbound document discovered by poll or webhook. This is the
 * ONLY thing a webhook is trusted to deliver (doorbell, not delivery — plan
 * §3d); document content always comes from `fetchDocument`.
 */
/**
 * What the VENDOR says a document is, in house format — recorded so an unrecognized
 * document is still described rather than reduced to 'UNK'.
 *
 * Every field is something the vendor already told us at discovery (Stedi's poll
 * response carries all of it), so this costs nothing to collect and answers most of
 * "what is this?" before anyone opens the payload.
 */
export type InboundDocumentDescriptor = {
  /** The artifact's MIME type as the vendor labels it ('application/edi-x12', …). */
  mimeType?: string;
  /** The transaction set the vendor reports ('835', '277', '864' — unmapped included). */
  transactionSet?: string;
  /** Implementation guide / version ('005010X214'). */
  release?: string;
  /** X12 functional group identifier ('HN', 'HC', …). */
  functionalGroup?: string;
  /** Artifact size in bytes, when the vendor states it. */
  sizeBytes?: number;
};

export type InboundDocumentRef = {
  /** Vendor's transaction id (account-unique; layer-1 dedupe key, plan §4.1). */
  partnerTransactionId: string;
  docType: IntegrationDocumentType;
  /**
   * When the vendor created/received the transaction — epoch ms (house time
   * format). Each vendor FACTORY maps its wire format here (Stedi: ISO
   * `createdAt` → `Date.parse`); vendor formats never cross this seam.
   */
  receivedAt?: number;
  /**
   * The vendor's own description of the document. Load-bearing ONLY for the
   * unidentified case: a docType of `Unknown` says what we could not map, and this says
   * what the vendor nonetheless told us about it.
   */
  descriptor?: InboundDocumentDescriptor;
};

/**
 * ONE page of a vendor poll (plan §6 INFRA-6). `poll` fetches a single page
 * per call — the drain owns the page loop, so it can persist the cursor and
 * enforce its page cap between calls (a connector-internal loop could
 * silently swallow both).
 */
export type ConnectorPollResult = {
  refs: InboundDocumentRef[];
  /**
   * The vendor's resume cursor: pass it back as `pageToken` to continue.
   * Persisted on the platform row as the PRIMARY watermark (plan §7 item 2) —
   * absence means the vendor did not advance the cursor, never "start over".
   */
  nextPageToken?: string;
};

/**
 * The persisted poll watermark — `specifics.pollCursor` on the vendor's
 * clinicId = 0 platform Integration row, written ONLY via
 * `setPlatformSpecificsKey` (plan §6 INFRA-6, §7 item 2). The drain advances
 * it only after a page's refs have all been ingested; a run that throws
 * leaves it untouched, so the next run re-reads what the failed run saw and
 * the dedupe gate swallows the overlap.
 */
export type ConnectorPollCursor = {
  /** Vendor's `nextPageToken` — the primary watermark. */
  pageToken?: string;
  /**
   * Recovery floor (ms): the newest instant known fully covered. When the
   * pageToken is missing or the vendor rejects it, polling restarts from
   * this minus the vendor's `polling.recoveryOverlapMs` and dedupe layer 1
   * makes the re-read harmless. Advances to the newest `receivedAt`
   * ingested; an empty (caught-up) page advances it to the poll time minus
   * the vendor's `polling.recencyExclusionMs`.
   */
  watermarkMs: number;
};

/**
 * Persisted per-vendor drain timing (`specifics.pollStats`, INFRA-6 —
 * Brian, 2026-07-21): an EWMA of per-document ingest wall-clock, carried
 * across runs so the drain's time-budget check ("can the NEXT document
 * finish before the Lambda deadline?") estimates from observed history
 * instead of a guess. Written only via `setPlatformSpecificsKey`.
 */
export type ConnectorPollStats = {
  /** EWMA of one document's ingest duration (wall-clock ms). */
  avgDocMs: number;
  /** Documents folded into the average, lifetime. */
  docs: number;
  updatedAt: number;
};

/**
 * Invocation budget forwarded unchanged through the inbound ingest call chain.
 * Only the document-specific final destination decides how to use an absent
 * deadline; intermediate layers must not invent or replace one.
 */
export type ConnectorInboundDispatchOptions = {
  /** Epoch-ms hard stop after the entry point's cleanup reserve. */
  deadlineMs?: number;
};

/**
 * Outbound request auth, per scheme not per vendor (plan §3b). `api-key` and
 * `basic` are implemented; `oauth2-client-credentials` is interface-only until
 * a vendor requires it. The authenticator that APPLIES these lives in
 * server-core's connector layer (it is axios-coupled).
 */
export type IntegrationApiAuth =
  | { scheme: 'api-key'; apiKey: string; header?: string; prefix?: string }
  | { scheme: 'basic'; username: string; password: string }
  | {
      scheme: 'oauth2-client-credentials';
      tokenUrl: string;
      clientId: string;
      clientSecret: string;
      scopes?: string[];
    }
  | { scheme: 'custom'; kind: string; params: Record<string, string> };

/**
 * Credentials resolved per the connector's scope (plan §3b): platform scope
 * reads the vendor's Secrets Manager secret; clinic scope reads the clinic's
 * `Integration.specifics`. Connectors receive these per call — they hold no
 * state of their own (Lambda warm-start rule).
 */
export type ConnectorCredentials = {
  auth: IntegrationApiAuth;
  /**
   * The named key this material came from (`specifics.keyRouting`, INFRA-11).
   * Names are admin-chosen; never key material.
   */
  keyName: string;
  /**
   * What this key's traffic IS (`specifics.keyMetadata[keyName].dataClass`):
   * `mock` = never contacts real payers (test-mode key) — trace/operations
   * detail may persist unscrubbed; `phi` (default) = today's scrubbing.
   */
  dataClass: 'phi' | 'mock';
  /**
   * Vendor API hosts, resolved from the platform row's
   * `Integration.specifics.endpoints` (the source of truth — no code default).
   * Polling can use a DIFFERENT host than claims/reports, so a call picks its
   * host by kind rather than sharing one base URL. Missing/invalid → the vendor
   * client throws (fail loud).
   */
  endpoints: {
    api: string;
    poll: string;
    /** Non-document transaction-enrollment API host, when the vendor provides one. */
    enrollment?: string;
    /** Vendor payor-directory API host, when the vendor provides one. */
    directory?: string;
    /** Real-time eligibility (270/271) host, when the vendor provides one. */
    eligibility?: string;
  };
};

/**
 * One interactive vendor request (enrollment, directory, eligibility, …). The
 * value key routing (`specifics.keyRouting`, INFRA-11) resolves a named key
 * from the operation's exchange type — see `exchangeTypeForOperation` in
 * server-core's `connectorRequestLifecycle.ts`, a TOTAL map: a new operation
 * does not compile until it declares its type.
 */
export enum ConnectorRequestOperation {
  ProviderRegistrationVerify = 'provider-registration.verify',
  ProviderRegistrationCancel = 'provider-registration.cancel',
  PayorDirectorySearch = 'payor-directory.search',
  PayorDirectoryRetrieve = 'payor-directory.retrieve',
  EnrollmentRequestSubmit = 'enrollment-request.submit',
  EnrollmentRegistrationRetrieve = 'enrollment-registration.retrieve',
  EnrollmentTaskComplete = 'enrollment-task.complete',
  EnrollmentDocumentUploadCreate = 'enrollment-document.upload-create',
  EnrollmentDocumentUpload = 'enrollment-document.upload',
  EnrollmentDocumentUploadConfirm = 'enrollment-document.upload-confirm',
  EnrollmentDocumentDownload = 'enrollment-document.download',
  EligibilityCheck = 'eligibility.check',
  /** Transmit worker: one claim to the vendor. */
  ClaimSubmit = 'claim.submit',
  /** Inbound drain: one poll sweep (many pages). */
  InboundPoll = 'inbound.poll',
  /** Ingest funnel / redispatch: fetch one discovered document. */
  InboundFetch = 'inbound.fetch',
  /** Credential check: probe one named key. */
  CredentialProbe = 'credential.probe',
}

export type ConnectorRequestTelemetry = {
  events: RecordAnalyticsParams[];
};

/** One HTTP call to a vendor, as the connector HTTP client sees it leave. */
export type ConnectorWireCall = {
  method: string;
  /** Path only — query strings and hosts stay off the trace. */
  path: string;
  startedAt: number;
};

/** How that call came back. */
export type ConnectorWireOutcome = {
  statusCode: number | null;
  durationMs: number;
  /** The transport/vendor error message when the call failed; absent on success. */
  error?: string;
};

/**
 * Hooks the connector HTTP client runs around EVERY wire call, reading and
 * writing the flow's context. Installed by chain steps (tracing, telemetry);
 * a vendor may append its own when it builds its client.
 */
export type ConnectorWireHooks = {
  before: Array<(context: ConnectorRequestContext, call: ConnectorWireCall) => void>;
  after: Array<
    (
      context: ConnectorRequestContext,
      call: ConnectorWireCall,
      outcome: ConnectorWireOutcome
    ) => void
  >;
};

/** How the flow ended — set by the runner before the teardown chain runs. */
export type ConnectorRequestOutcome =
  | { status: 'success'; durationMs: number; result: unknown }
  | { status: 'error'; durationMs: number; error: unknown };

/**
 * The engrams of one vendor flow: built by the request runner, mutated in
 * order by the chain's setup steps (credentials, trace, wire hooks), threaded
 * explicitly through the connector method and the vendor service to the HTTP
 * client, read by the teardown steps (flush telemetry, flush trace). Nothing
 * about a wire flow lives anywhere else — no module state, no GLOBALS.
 */
export type ConnectorRequestContext = {
  connection: Connection;
  vendor: KnownClearingHouses;
  scope: ConnectorScope;
  operation: ConnectorRequestOperation;
  /** What the call IS for key routing; defaults from the operation, overridable per run. */
  exchangeType: IntegrationExchangeTypeEnum;
  /** Skip key routing and use this named key (credential probes only). */
  keyName?: string;
  clinicId?: number;
  integrationId?: number | null;
  /** The exchange row this flow is about, when there is one — stamped on trace steps. */
  exchangeId?: string | null;
  /** Stamped-row tracing: when no vendor session is live, trace under this row's stamp. */
  resumeTraceUntil?: number | null;
  /** Free-form `code` for the vendor-call telemetry row (defaults to the operation). */
  telemetryCode?: string;
  startedAt: number;
  telemetry: ConnectorRequestTelemetry;
  /** Set by the credentials step; the HTTP client refuses to exist without it. */
  credentials?: ConnectorCredentials;
  /**
   * The trace collector this flow records under — opened by the trace step, or
   * inherited from an enclosing flow (then `ownsTrace` is false and the
   * teardown does not flush it).
   */
  trace?: ConnectorTraceCollector;
  ownsTrace?: boolean;
  wire: ConnectorWireHooks;
  outcome?: ConnectorRequestOutcome;
};

export type AuthenticatedConnectorRequestContext = ConnectorRequestContext & {
  credentials: ConnectorCredentials;
};

/** One step of the request chain — reads/mutates the context, returns nothing. */
export type ConnectorChainStep = (context: ConnectorRequestContext) => Promise<void> | void;

/**
 * The ordered functions a vendor flow runs around its work: `setup` before the
 * request (build credentials, open trace, install wire hooks…), `teardown`
 * after it — success or failure — (flush telemetry, flush trace…).
 */
export type ConnectorRequestChain = {
  setup: ConnectorChainStep[];
  teardown: ConnectorChainStep[];
};

/**
 * Signals a document offers for deciding WHICH CLINIC it belongs to (plan §3e.1).
 * House format: the router consumes only this, never a vendor shape. Declared here
 * rather than in server-core so the vendor contract below can return it.
 */
export type InboundRoutingSignals = {
  /** CLM01s from the document (payer-echoed patient control numbers). */
  patientControlNumbers?: string[];
  /** Vendor transaction ids the document references (e.g. a 277's original-claim reference). */
  referencedTransactionIds?: string[];
  payeeNpi?: string | null;
  payeeTin?: string | null;
  /** The echoed REF*EA value — our token or the clinic's account number. */
  patientRef?: string | null;
};

/**
 * A submitted document's contribution to the `claim/submission` activity row.
 * Field names mirror `RecordAnalyticsParams` so the core spreads them as-is.
 */
export type ClaimSubmissionAnalytics = {
  /** Claims (CLM segments) in the document. */
  count: number;
  /** Total charge $ those claims requested (CLM02). */
  sum: number;
};

/** The X12 segment createBatchSet is about to append to a claim. */
export type ConnectorClaimSegmentContext = {
  segmentId: string;
  loop: string;
  elements: readonly string[];
};

/** Result of submitting ONE claim (one exchange row = one claim, plan §3c). */
export type ClaimSubmissionResult = {
  status: 'submitted' | 'rejected';
  /**
   * Vendor's id for the accepted claim — stored in
   * `IntegrationExchange.partnerTransactionId` (the 277CA correlation key,
   * plan §3c.2; the invoice reaches it via `Invoice.exchangeId`).
   */
  partnerTransactionId?: string;
  /** Vendor edit/validation rejections, verbatim. Never logged with PHI. */
  errors?: unknown[];
  /** Vendor's full response body for `meta` bookkeeping. */
  raw?: unknown;
};

/**
 * A TERMINAL submission failure handed to a vendor's `isaClaimIssue` /
 * `submissionFailureIssues`. Core-safe PRIMITIVES only — this contract lives in
 * `libs/core` and cannot import server-core's `IntegrationApiError`, so the
 * transmit core flattens the thrown error or rejected result into these fields
 * before asking the vendor to interpret it. Transient failures (5xx/429/network)
 * never arrive here: they stay `Pending` and retry.
 */
export type ConnectorSubmissionFailure =
  | {
      /** The vendor call threw a non-transient HTTP error (e.g. a 400 validation reject). */
      kind: 'thrown';
      statusCode?: number;
      /** Vendor error body — MAY echo PHI; the vendor distills PHI-free issues from it, never copies it. */
      responseBody?: unknown;
      message?: string;
    }
  | {
      /** The vendor returned 2xx but the body rejected the claim (e.g. an error array). */
      kind: 'rejected';
      errors?: unknown[];
      raw?: unknown;
    };

/**
 * One fetched inbound document: the vendor's COMPLETE report response,
 * verbatim (`raw` — persisted through the payload envelope, never trimmed; plan §3d),
 * plus the canonical structure the existing handlers consume (`payload` — e.g.
 * a pre-parsed advice for `handle835Response`).
 */
export type CanonicalInboundDocument = {
  docType: IntegrationDocumentType;
  payload: unknown;
  raw: unknown;
};

/**
 * Outcome of one real-time eligibility check, in house vocabulary. The status
 * values are the request row's own (`InsuranceEligibilityRequestStatus`).
 */
/**
 * `IntegrationExchangePayload.contextMimeType` version for a real-time
 * eligibility response stored as the 270 row's paired context. Eligibility is
 * one synchronous call, so the response is the request's outcome, not a second
 * exchange: body = the document sent, context = the vendor's answer verbatim.
 */
export const ELIGIBILITY_RESPONSE_CONTEXT_VERSION = 'elig-res-v1';

export type ConnectorEligibilityResult = {
  status:
    | InsuranceEligibilityRequestStatus.Processed
    | InsuranceEligibilityRequestStatus.Rejected
    | InsuranceEligibilityRequestStatus.Error;
  /**
   * The house 271 model the eligibility UI renders. Processed: the benefits.
   * Rejected: the code-'V' rejection group, built exactly as `Parser271` builds
   * it from AAA segments, so a payer rejection renders identically on both
   * transports. Error: absent — there is nothing to render.
   */
  response?: EligibilityBenefitResponse;
  /** PHI-free reasons for Rejected/Error, the shape `submissionFailureIssues` returns. */
  issues: BusinessRuleItemType[];
  /** The vendor's identifier for this transaction, when its response carries one. */
  vendorTransactionId?: string;
  /**
   * Set when the vendor was really called but with ITS OWN test fixture
   * subscriber (a test-mode key answers real subscribers with a rejection and
   * nothing else). `response` carries the requested patient's identity over the
   * fixture's benefits. The house marks the request (`mock-` control number,
   * notes, message) and does not treat the answer as proof of a payer route.
   */
  simulated?: {
    /** Which fixture was sent, e.g. `stedi:60054:john-doe`. */
    fixture: string;
    /** The payer id the fixture went to (not the patient's payer). */
    sentTo: string;
  };
};

export type ConnectorClinicAccessOptions = {
  connection: Connection;
  clinicId: number;
};

export type ConnectorClaimTransferOptions = ConnectorClinicAccessOptions & {
  exchangeId: string;
};

export type ConnectorEligibilityTransferOptions = {
  clinicId: number;
  exchangeId: string;
};

export type ConnectorInboundTransferOptions = ConnectorClaimTransferOptions & {
  document: CanonicalInboundDocument;
};

export interface IntegrationConnector {
  vendor: KnownClearingHouses;
  scope: ConnectorScope;
  capabilities: ConnectorCapabilities;

  /** Whether enrollment is available before a clinic selects this vendor on a billing profile. */
  enrollmentWithoutBillingProfile?: boolean;
  /** Optional clinic entitlement policy; absent means no additional vendor access gate. */
  hasClinicAccess?(options: ConnectorClinicAccessOptions): Promise<boolean>;
  /** Known vendor transfers, recorded independently of subsequent local processing. */
  recordClaimTransfer?(options: ConnectorClaimTransferOptions): Promise<void>;
  recordEligibilityTransfer?(options: ConnectorEligibilityTransferOptions): Promise<void>;
  recordInboundTransfer?(options: ConnectorInboundTransferOptions): Promise<void>;

  /**
   * What the vendor's SUBMISSION actually consumes from the X12 interchange
   * envelope. createBatchSet assembles X12 on every path, but a vendor may
   * transcribe only the CLAIM loops to its own document and discard the
   * envelope — so envelope-only fields (e.g. ISA06 site id) are irrelevant to
   * it. Declared per vendor (required, like `polling`): each vendor states its
   * OWN envelope needs; assuming another vendor's requirements is the bug.
   * createBatchSet consults this instead of hardcoding one behavior for all
   * connectors.
   */
  x12: {
    /**
     * Does this vendor's submission consume the X12 INTERCHANGE/functional-group
     * ENVELOPE — the ISA/GS wrapper fields on the Integration row:
     * senderIdQualifier, senderId/site id (ISA06,
     * testSiteId/productionSiteId), receiverIdQualifier, receiverId,
     * appReceiverCode, exchangeVersionCode?
     *
     * false when the vendor transcribes only the CLAIM loops to its own
     * document and discards the envelope (Stedi: submitter comes from the 837
     * 1000A loop, payer from 2010BB — the ISA/GS wrapper is thrown away). Then a
     * missing/blank envelope field must NOT block the build.
     *
     * true for envelope-sensitive transports (SFTP semantics), which
     * DO require those fields. Declared per vendor (required, like `polling`):
     * each vendor states its own; assuming another's behavior is the bug.
     */
    requiresEnvelope: boolean;
  };

  /**
   * Vendor-documented polling characteristics — facts about the VENDOR's
   * feed, not drain policy, so they live in the factory and every new vendor
   * must state its own (required on purpose: silently inheriting another
   * vendor's numbers is the bug).
   */
  polling: {
    /**
     * Feed recency exclusion: same-instant transactions can surface this
     * late, so an empty page proves coverage only to poll-time minus this
     * window — the drain's watermark never advances into it.
     */
    recencyExclusionMs: number;
    /**
     * Overlap below the watermark floor on token-less recovery — how far
     * before the last processed transaction the vendor recommends
     * restarting; dedupe layer 1 makes the re-read a silent skip.
     */
    recoveryOverlapMs: number;
  };

  /**
   * The vendor-specific renderer (plan §3c.1, journal Resolved 21): takes
   * createBatchSet's IN-MEMORY X12 output — the artifact every CBS decision
   * path produced — and returns the vendor's SUBMISSION DOCUMENT (Stedi: the
   * literal professional-claims request body). Implemented as vendor-neutral
   * `parse837` + a table-driven vendor codec: a transcription of what CBS
   * emitted, never a re-derivation, so CBS stays the single source of truth.
   * The x12 input is transient and NEVER stored; the returned document is
   * persisted verbatim through the payload envelope and transmitted byte-identically.
   * Pure and synchronous — render failures surface at build time, inside the
   * build transaction. Throws on documents it cannot render.
   */
  renderClaim(args: { x12: string }): unknown;

  /**
   * Additional vendor edits for the X12 segment createBatchSet just built.
   * CBS remains responsible for traversing the claim and recording returned
   * strings through its normal business-rule pipeline.
   */
  validateSegment?(context: ConnectorClaimSegmentContext): string[];

  /**
   * What the transmitted document contributes to `claim/submission` analytics.
   * The vendor owns it because only the vendor knows its document shape — the
   * transmit core records the numbers without ever reading the payload, and
   * without re-querying figures the document already carries.
   *
   * Pure and synchronous, and read from the SAME bytes that went to the vendor,
   * so the metric counts what was actually sent. Must not throw: a vendor that
   * cannot find its figures returns `{ count: 0, sum: 0 }` and the core skips
   * the row rather than failing a transmit that already succeeded.
   */
  claimSubmissionAnalytics(args: { document: unknown }): ClaimSubmissionAnalytics;

  /**
   * The clinic-routing signals an INBOUND document offers, in house format.
   *
   * The vendor owns it because the signals live in vendor-shaped fields that the
   * canonical payload does not carry — a 277CA's payee NPI and its
   * `claimTransactionBatchNumber` (our own submission stamp) survive nowhere in the
   * parser shape the dispatcher consumes. Reading them in the funnel would put a
   * vendor's field names in vendor-agnostic code.
   *
   * Pure and synchronous. Return `{}` for a doc type offering nothing — the router
   * treats absent signals as Unrouted, which is the safe outcome.
   */
  inboundRoutingSignals(args: {
    docType: IntegrationDocumentType;
    /** The vendor's response, verbatim. */
    raw: unknown;
    /** `parseDocument`'s output for that raw. */
    payload: unknown;
  }): InboundRoutingSignals;

  submitClaim(args: {
    context: AuthenticatedConnectorRequestContext;
    /** `renderClaim`'s output, read back through the payload envelope — transmitted verbatim. */
    document: unknown;
    /**
     * TRANSMISSION identity, not claim or encounter identity (plan §3c.3):
     * always `exchangeId`, so transport retries of one delivery (SQS
     * redelivery, worker crash, backstop re-enqueue) collapse into ONE claim
     * at the vendor, while every new submission — corrected resubmission,
     * secondary after primary — is a new exchange row and therefore a fresh
     * key that the vendor's idempotency window must NOT swallow. NEVER key
     * this on `billingKey`: encounter scope would silently eat corrections
     * and collide primary/secondary claims inside the vendor's 24 h window.
     */
    idempotencyKey: string;
  }): Promise<ClaimSubmissionResult>; // one exchange row = one claim (§3c)

  /**
   * Per-vendor classification of a TERMINAL submission failure (plan §3c;
   * no-uniform-connector-behavior — a 400 is a clinic-addressable reject for one
   * vendor, a 2xx-with-error-array for another). TRUE only when the vendor is
   * SURE the failure is a CLAIM issue the clinic must correct and resubmit;
   * anything else (auth/routing, ambiguous) is a CONNECTION issue —
   * `isaConnectionIssue` is simply `!isaClaimIssue` (the reverse convenience,
   * one source of truth). The transmit core routes a claim issue onto the
   * INVOICE as `Rejected`; a connection issue onto the exchange's
   * `meta.dispositionHistory`. The conservative default when unsure is `false`:
   * a wrongly-`Rejected` invoice is worse than a connection note.
   */
  isaClaimIssue(failure: ConnectorSubmissionFailure): boolean;

  /**
   * The vendor's PHI-FREE distillation of a terminal failure into
   * `BusinessRuleItemType` issues — the recovery information the biller (claim
   * issue) or operator (connection issue) reads. The vendor owns it because only
   * it knows its error shape; it MUST NOT copy raw response bytes that echo
   * patient identifiers (those stay on `meta.phi`). May return [] when it has
   * nothing beyond the status the core already records.
   */
  submissionFailureIssues(failure: ConnectorSubmissionFailure): BusinessRuleItemType[];

  /**
   * The embedded 277CA an edit rejection carries, as X12 text — when the vendor
   * returns one. Stedi answers a processed-but-edit-failed submission with
   * HTTP 200 + `errors[]` + its initial 277CA in the `x12` property (confirmed
   * with Stedi 2026-08-11); `errors[]` names the failing service line only in
   * prose, so the STC loops in this document are the ONLY structured per-line
   * source on the API channel. Undefined when the response carries none (e.g. a
   * malformed-request 400, where edits never ran). The core applies it through
   * `applyClaimAcknowledgements` — the same consumer an SFTP 277CA gets.
   */
  submissionRejection277CA(failure: ConnectorSubmissionFailure): string | undefined;

  /**
   * Does this vendor support RE-DRIVING this exchange in its CURRENT state? The
   * VENDOR decides — not generic "it's a connector" logic — because retry
   * support varies by type + disposition + direction (Stedi re-drives a parked
   * outbound claim and re-dispatches a parked inbound report; a future vendor
   * may differ). The server-core `isExchangeRetryable` composite calls this for
   * connector exchanges; BOTH backends go through that one composite — the GET
   * read that stamps `retryable` and the POST retry endpoint that enforces it —
   * so visibility and enforcement can't disagree. Takes the WHOLE exchange. Pure.
   */
  isExchangeRetryable(exchange: IntegrationExchangeRow | IntegrationExchangeRowPartial): boolean;

  /**
   * ONE page of the vendor's account feed (plan §6 INFRA-6). `pageToken`
   * (the persisted watermark) wins when present; `sinceMs` is the
   * floor-recovery/first-run start when there is no token — epoch ms, the
   * house time format; each connector converts to whatever its vendor's API
   * wants (Stedi: ISO-8601 `startDateTime`). The drain owns the page loop,
   * cursor persistence, and the page cap.
   */
  poll(args: {
    context: AuthenticatedConnectorRequestContext;
    sinceMs: number; // start when no pageToken (platform-level for platform-scoped vendors)
    pageToken?: string;
  }): Promise<ConnectorPollResult>;

  /**
   * One cheap authenticated call proving the given key works for the given
   * exchange type (INFRA-11 per-key health). Probe choice is vendor knowledge
   * — a test-mode key must be probed with a call its capability set allows,
   * never a production-only endpoint. Absent, or resolving to no probe for
   * the type, the key is SKIPPED (its `probed` metadata stays null) rather
   * than handed a false verdict. A 2xx return = valid; a thrown
   * IntegrationApiError carries the status the caller classifies (401/403 =
   * invalid, else unreachable).
   */
  credentialProbe?(args: {
    context: AuthenticatedConnectorRequestContext;
    exchangeType: IntegrationExchangeTypeEnum;
  }): Promise<void> | undefined;

  fetchDocument(args: {
    context: AuthenticatedConnectorRequestContext;
    ref: InboundDocumentRef;
  }): Promise<CanonicalInboundDocument>;

  /**
   * The PARSE half of `fetchDocument`, standalone: vendor report JSON → the
   * canonical structure the existing handlers consume. PURE — no vendor
   * call — so the retry path can rebuild a document from the STORED payload
   * (the raw report persisted at ingest) without re-fetching bytes that
   * must stay identical to what was hashed. `fetchDocument` composes fetch +
   * this. Throws on doc types the vendor cannot parse.
   */
  parseDocument(args: { docType: IntegrationDocumentType; raw: unknown }): unknown;

  /**
   * The vendor's CURRENT transaction-set → docType mapping, queryable one id at
   * a time (e.g. '835' → Remit835; '864' → undefined until an 864 story ships).
   * Exists so a document parked `Unsupported` as `'UNK'` — its docType frozen in
   * meta at ingest, before a mapping existed — can be RE-derived from its stored
   * descriptors at reprocess time and become re-drivable on the deploy that adds
   * the mapping, with no manual step. Undefined means "this vendor still cannot
   * name that set"; the row stays parked.
   */
  docTypeForTransactionSet(transactionSetIdentifier: string): IntegrationDocumentType | undefined;

  /**
   * Layer-3 content identity for ONE fetched document (plan §4.3) — each
   * vendor picks its own canonicalization rather than a one-size-fits-all
   * hash. Return a LABELED hash (`<label>:<hex>`, e.g. `v1:<md5>`) or null
   * to opt the doc type out of content dedupe (layers 1–2 still apply;
   * remits then park on ANY TRN recurrence — the conservative disposition).
   * The label partitions hash spaces — two canonicalizations, or a
   * canonicalization and the SFTP-channel X12 guard's bare md5s sharing the
   * `checksum_fuzzy` column, can never cross-match — and the layer-3 lookup
   * REFUSES unlabeled values. Pure and synchronous.
   */
  calculateChecksumFuzzy(args: { document: CanonicalInboundDocument }): string | null;

  /**
   * Optional local testing aid. The vendor owns both the supported scenarios
   * and its native 835 representation; generic code never fabricates a vendor
   * payload shape. The returned value is fed back through parseDocument and
   * the normal inbound connector funnel.
   */
  pseudo835?: {
    scenarios: Pseudo835ScenarioOption[];
    /** Vendor-owned proof that this acknowledgement can seed a pseudo remit. */
    canGeneratePseudo835(args: { evidence: unknown }): boolean;
    render(args: {
      claimDocument: unknown;
      scenario: Pseudo835Scenario;
      transactionId: string;
      remittanceDate: string;
    }): unknown;
  };

  /**
   * Optional real-time eligibility (270/271) capability. The vendor owns the
   * wire: its request document, the synchronous call, and the transcription of
   * its response into the house `EligibilityBenefitResponse` — the same model
   * `parse271().resolveResponse()` produces, so the eligibility UI renders both
   * transports with no branch.
   */
  eligibility?: {
    /**
     * Pure. The vendor's request document from the shared eligibility context
     * and the payer id the VENDOR knows (`ConnectionApiMapping.canonicalPayorId`
     * first; see `resolveConnectorPayerId`). Persisted verbatim as the outbound
     * `270` exchange payload.
     */
    render(args: { context: EligibilityRequestContext; tradingPartnerServiceId: string }): unknown;
    /**
     * Resolve the payer id → render → call the vendor → normalize. Returns the
     * payer id it put on the wire, the rendered document and the vendor's
     * verbatim response alongside the result so the caller can persist both
     * sides of the exchange and record the proven route.
     */
    check(args: {
      context: AuthenticatedConnectorRequestContext;
      eligibility: EligibilityRequestContext;
      /**
       * The upstream network chain forwarded as `X-Forwarded-For` — CMS/HETS
       * requires the originating IP chain on every Medicare inquiry. Never
       * logged or persisted.
       */
      forwardedFor?: string;
    }): Promise<{
      /** The vendor payer id used (`resolveConnectorPayerId`). */
      payerId: string;
      document: unknown;
      raw: unknown;
      result: ConnectorEligibilityResult;
    }>;
    /**
     * Pure. The vendor's verbatim response → house model + outcome. Re-runnable
     * on a stored response with no vendor call.
     */
    normalize(args: { raw: unknown }): ConnectorEligibilityResult;
  };
}
